import { type IDocument } from "@types";

import { isAdviceOrReviewPurchaseType } from "./products";
import { documentStatuses } from "@shared/constants/documentStatuses";

// Advice/review purchases include a lawyer meeting instead of a separate payment step, so the
// waiting-for-payment status coming from the application API is replaced with waiting-for-meeting.
export const remapAdviceOrReviewDocumentStatuses = <
  T extends Pick<IDocument, "status" | "statuses" | "purchasedAsType">,
>(
  document: T,
): T => {
  if (!isAdviceOrReviewPurchaseType(document.purchasedAsType)) {
    return document;
  }

  const isMeetingInStatuses = document.statuses?.some(
    (documentStatus) => documentStatus.status === documentStatuses.waitingForMeeting,
  );
  const isAwaitingMeeting = document.status === documentStatuses.waitingForPayment;
  const paymentColour = document.statuses?.find(
    (documentStatus) => documentStatus.status === documentStatuses.waitingForPayment,
  )?.colour;

  const statuses = isMeetingInStatuses
    ? document.statuses
        ?.filter((documentStatus) => documentStatus.status !== documentStatuses.waitingForPayment)
        // When the dropped payment step was the active one, waiting-for-meeting takes its place
        // and must keep its backend colour — the backend marks the active step, not the meeting.
        .map((documentStatus) =>
          isAwaitingMeeting && documentStatus.status === documentStatuses.waitingForMeeting && paymentColour != null
            ? { ...documentStatus, colour: paymentColour }
            : documentStatus,
        )
    : document.statuses?.map((documentStatus) =>
        documentStatus.status === documentStatuses.waitingForPayment
          ? { ...documentStatus, status: documentStatuses.waitingForMeeting }
          : documentStatus,
      );

  return {
    ...document,
    status: isAwaitingMeeting ? documentStatuses.waitingForMeeting : document.status,
    statuses,
  };
};

export const getDocumentOwnerNames = (document: IDocument): string[] =>
  [document.primaryOwnerName ?? document.fullname, document.secondaryOwnerName].filter(
    (name): name is string => name != null && name.length > 0,
  );
